import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { Tooltip } from "@blueprintjs/core";
import { uniq } from "lodash";
import { GlobalEntityType } from "shared/types";
import { getNumberFromNumberString, renderNumberValue } from "utils/string";
import ResultInfoMessage, { ResultInfoMessageType } from "components/ResultInfoMessage";
import LineGraphElementConfig from "./components/LineGraphElementConfig";
import CreateVariantFromResultElementDialog from "../../../CreateVariantFromResultElementDialog";
import CreateEntityFromResultElementDialog from "../../../CreateEntityFromResultElementDialog";
import IconButton from "components/IconButton";
import DefaultThreeDotsMenuItem from "components/ThreeDotsMenu/Components/DefaultThreeDotsMenuItem";
import GraphElementGraph from "../GraphElementGraph";
import { handlePlotHover, prepareLineGraphData, preparePlotLayout, prepareRangeConfigsOnLockButtonClick, } from "../GraphElementGraph/utils";
import GraphSourcesLabels from "../GraphSourcesLabels";
import { calculationLineGraphElementResultSelector, createEntityFromResultElementDialogDataSelector, isResultElementChangingSelector, isResultFetchingSelector, } from "store/rs-workspace/selectors/results.selectors";
import { EntitiesCreatableFromResultElement, ResultElementGraphViewType, ResultElementLineType, ResultElementParameterType, ResultElementSourceType, ResultSetElementType, SimulationRunState, } from "store/rs-workspace/rs-workspace.types";
import { SourceFields, SourceSourceFields } from "../ProcessDataViewerElement/types";
import { getCombinationParameterPreview } from "store/rs-workspace/utils/common.utils";
import { persistedInsightsDataSelector, workspaceInsightsSelector, workspaceNotRefinedCellModelsSelector, workspaceOptimizationsSelector, workspaceProcessDataSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { parameterTypesSelector, processVariablesSelector } from "store/global/global.selectors";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { setCreateEntityFromResultElementDialogData, setPlotRangeConfigs, updatePlotSourceConfigs, } from "store/rs-workspace/actions/results.actions";
import { getAlternativeStepFlowsList } from "store/recipe/recipe.utils";
import { getSimulationSourceName } from "store/rs-workspace/utils/scenario.utils";
import { getResultElementSourceComputedName, getSimulationTimestampOptions, getSourceLineColor, getTimingSummaryText, } from "store/rs-workspace/utils/results.utils";
import { copyToClipboard } from "utils/data";
import { getTextContentFromHtml } from "utils/string";
import { usePrevious } from "hooks/usePrevious";
import useMessageTimer from "hooks/useMessageTimer";
import { NewEntityCreationOption } from "shared/labels";
import AutoscaleIcon from "icons/autoscale.svg";
import GridlinesIcon from "icons/grid.svg";
import ErrorIcon from "icons/info_triangle.svg";
import PlusIcon from "icons/plus.svg";
import * as styles from "./styles.module.scss";
export const defaultSource = {
    [SourceFields.view]: true,
    [SourceFields.source]: null,
    [SourceFields.viewType]: ResultElementGraphViewType.MedianWithSpread,
    [SourceFields.lineType]: ResultElementLineType.Unknown,
};
const LineGraphElement = ({ form, elementData, restrictions, resultSetId }) => {
    const dispatch = useDispatch();
    const updatedRangeConfigsRef = useRef();
    const [graphData, setGraphData] = useState(null);
    const [newEntityType, setNewEntityType] = useState(null);
    const [createEntityBaseSource, setCreateEntityBaseSource] = useState(undefined);
    const [selectedGraphValue, setSelectedGraphValue] = useState(null);
    const [layoutRevisionNo, setLayoutRevisionNo] = useState(0);
    const [showCopiedToClipboardMessage, setShowCopiedToClipboardMessage] = useMessageTimer();
    const isResultFetching = useSelector(isResultFetchingSelector);
    const parameterTypes = useSelector(parameterTypesSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const cellModels = useSelector(workspaceNotRefinedCellModelsSelector);
    const simulations = useSelector(workspaceSimulationsSelector);
    const optimizations = useSelector(workspaceOptimizationsSelector);
    const insights = useSelector(workspaceInsightsSelector);
    const persistedInsightsData = useSelector(persistedInsightsDataSelector);
    const processVariables = useSelector(processVariablesSelector);
    const processData = useSelector(workspaceProcessDataSelector);
    const isResultElementChanging = useSelector(isResultElementChangingSelector(resultSetId, elementData.id));
    const createEntityDialogData = useSelector(createEntityFromResultElementDialogDataSelector);
    const pageInEditMode = useSelector(isEditableSelector);
    const graphResults = useSelector(calculationLineGraphElementResultSelector({ resultSetId, resultSetElementId: elementData.id }, ResultSetElementType.LineGraph));
    const prevIsResultFetching = usePrevious(isResultFetching);
    const prevIsResultElementChanging = usePrevious(isResultElementChanging);
    const someResultBiased = useMemo(() => graphResults?.some(({ source, hasErrors }) => source.view && hasErrors), [graphResults]);
    const editMode = useMemo(() => !!form, [form]);
    const xAxisIsDiscrete = useMemo(() => elementData.parameter.parameter?.type === ResultElementParameterType.ALTERNATIVE_FLOW, [elementData.parameter.parameter?.type]);
    const alternativeFlowsMapping = useMemo(() => xAxisIsDiscrete
        ? getAlternativeStepFlowsList(recipes, elementData.parameter?.parameter?.name).reduce((acc, name, index) => {
            if ((elementData.parameter?.parameter ?? {}).flowIndexes?.includes(index)) {
                acc.push({ idx: index, name });
            }
            return acc;
        }, [])
        : undefined, [elementData.parameter?.parameter, recipes, xAxisIsDiscrete]);
    const preparedParameter = useMemo(() => getCombinationParameterPreview(elementData.parameter, recipes, cellModels, parameterTypes), [cellModels, elementData.parameter, parameterTypes, recipes]);
    const parameterName = useMemo(() => preparedParameter?.parameter?.name, [preparedParameter]);
    const parameterUnit = useMemo(() => xAxisIsDiscrete
        ? ""
        : preparedParameter?.parameter?.units, [preparedParameter?.parameter, xAxisIsDiscrete]);
    const plotLayout = useMemo(() => preparePlotLayout(graphResults, elementData.xPlotConfig, updatedRangeConfigsRef.current, { xaxis: { title: `${parameterName} ${parameterUnit ? `(${parameterUnit})` : ""}` } }, xAxisIsDiscrete, alternativeFlowsMapping), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        elementData.xPlotConfig,
        graphResults,
        parameterName,
        parameterUnit,
        xAxisIsDiscrete,
        alternativeFlowsMapping,
        layoutRevisionNo,
    ]);
    const isAlternativeFlowParameter = useMemo(() => elementData?.parameter?.parameter?.type === ResultElementParameterType.ALTERNATIVE_FLOW, [elementData?.parameter?.parameter?.type]);
    const getActionMenuItems = useCallback((selectedPoints) => {
        const singleSourceAvailable = graphResults?.filter(({ source, state }) => source.view &&
            source.source?.type === ResultElementSourceType.Simulation &&
            state === SimulationRunState.FINISHED)?.length === 1;
        return selectedPoints.reduce((acc, point) => {
            const graphResult = graphResults?.find(({ sourceId }) => sourceId === point.customdata);
            const graphResultIndex = graphResults?.findIndex(({ sourceId }) => sourceId === point.customdata);
            const { source } = { ...graphResult };
            const canCreateEntityFromSource = graphResult?.state === SimulationRunState.FINISHED &&
                source?.source?.type === ResultElementSourceType.Simulation;
            const regularValue = Number(point?.x);
            const alternativeFlowId = Math.round(Number(point?.x));
            const value = isAlternativeFlowParameter ? alternativeFlowId : regularValue;
            const sourceName = getSimulationSourceName(simulations, optimizations, source?.source?.simulationId, source?.source?.optimizationId, source?.source?.adjustmentId);
            if (!canCreateEntityFromSource) {
                return acc;
            }
            const commonOnClickHandler = () => {
                setCreateEntityBaseSource(source);
                setSelectedGraphValue(value);
                dispatch(setCreateEntityFromResultElementDialogData({
                    elementId: elementData.id,
                    sourceId: source?.id ?? 0,
                }));
            };
            const internalMenuItems = [
                {
                    text: NewEntityCreationOption.Variant,
                    onClick: () => {
                        setNewEntityType(EntitiesCreatableFromResultElement.Variant);
                        commonOnClickHandler();
                    },
                    icon: React.createElement(PlusIcon, { width: 15, height: 15 }),
                },
                {
                    text: NewEntityCreationOption.Recipe,
                    onClick: () => {
                        setNewEntityType(EntitiesCreatableFromResultElement.Recipe);
                        commonOnClickHandler();
                    },
                    icon: React.createElement(PlusIcon, { width: 15, height: 15 }),
                },
                {
                    text: NewEntityCreationOption.CellModel,
                    onClick: () => {
                        setNewEntityType(EntitiesCreatableFromResultElement.CellModel);
                        commonOnClickHandler();
                    },
                    icon: React.createElement(PlusIcon, { width: 15, height: 15 }),
                },
            ];
            if (singleSourceAvailable) {
                return internalMenuItems;
            }
            return [
                ...acc,
                {
                    text: `Create from ${sourceName}`,
                    icon: (React.createElement("span", { className: styles.circle, style: {
                            backgroundColor: getSourceLineColor(source?.color, graphResultIndex),
                        } })),
                    classes: {
                        iconClassName: styles.menuCircleIcon,
                        className: styles.menuItem,
                    },
                    children: (React.createElement(React.Fragment, null, internalMenuItems.map((item) => (React.createElement(DefaultThreeDotsMenuItem, { key: item.text, ...item }))))),
                },
            ];
        }, []);
    }, [dispatch, elementData.id, graphResults, isAlternativeFlowParameter, optimizations, simulations]);
    const getRegularGraphTitleInnerHtml = useCallback((legendTitleInnerHtml) => {
        const xValue = renderNumberValue(getNumberFromNumberString(legendTitleInnerHtml));
        const unit = parameterUnit ? `(${parameterUnit.replaceAll("span", "tspan")})` : "";
        return `${parameterName}: ${xValue} ${unit}`;
    }, [parameterName, parameterUnit]);
    const getDiscreteGraphTitleInnerHtml = useCallback((ev) => {
        const eventXPoints = uniq(ev?.points?.map(({ x }) => Math.floor(Number(x))));
        if (eventXPoints.length === 1 && parameterName) {
            const flowName = alternativeFlowsMapping?.find((_, idx) => idx === eventXPoints[0])?.name;
            return `${parameterName} ${flowName ? `: ${flowName}` : ""}`;
        }
        return parameterName ?? "";
    }, [alternativeFlowsMapping, parameterName]);
    const checkActionMenuAvailable = useCallback((points) => {
        if (!pageInEditMode || !graphResults?.length || !points?.length) {
            return false;
        }
        return points.some((point) => {
            const result = graphResults.find(({ sourceId }) => sourceId === point.customdata);
            return !!(result?.source.view &&
                result?.source.source?.type === ResultElementSourceType.Simulation &&
                result?.state === SimulationRunState.FINISHED);
        });
    }, [graphResults, pageInEditMode]);
    const onPlotHover = useCallback((plotRef) => (ev) => handlePlotHover(plotRef, (legendTitleInnerHtml) => !xAxisIsDiscrete
        ? getRegularGraphTitleInnerHtml(legendTitleInnerHtml)
        : getDiscreteGraphTitleInnerHtml(ev), ev, checkActionMenuAvailable), [getDiscreteGraphTitleInnerHtml, getRegularGraphTitleInnerHtml, checkActionMenuAvailable, xAxisIsDiscrete]);
    const handleRelayout = useCallback((ranges, shouldUpdateLayout) => dispatch(updatePlotSourceConfigs({
        payload: {
            elementType: elementData.type,
            elementId: elementData.id,
            resultSetId,
            ranges,
        },
        callback: (updatedRangeConfigs) => {
            updatedRangeConfigsRef.current = updatedRangeConfigs;
            shouldUpdateLayout && setLayoutRevisionNo((prevState) => prevState + 1);
        },
    })), [dispatch, elementData.id, elementData.type, resultSetId]);
    const onLockButtonClick = useCallback((locked, axisName, responseId) => {
        handleRelayout(prepareRangeConfigsOnLockButtonClick(plotLayout, updatedRangeConfigsRef, locked, axisName, responseId), true);
    }, [handleRelayout, plotLayout]);
    const renderHeader = useCallback((isAllAxesAutoscaled, isAllGridlinesVisible, handleAutoscaleButtonClick, handleGridlinesButtonClick) => (React.createElement("div", { className: styles.top },
        someResultBiased && (React.createElement("div", { className: classNames(styles.biasedInfo, { [styles.shifted]: (graphResults?.length ?? 0) > 1 }) },
            React.createElement(Tooltip, { minimal: true, content: "At least one calculated value is biased.", placement: "bottom", popoverClassName: styles.tooltip, rootBoundary: "viewport", openOnTargetFocus: false },
                React.createElement(ErrorIcon, { className: styles["error-icon"] })))),
        React.createElement(IconButton, { classes: { root: styles.autoscaleIcon }, icon: React.createElement(AutoscaleIcon, { className: classNames({ [styles["autoscaleIcon-icon-autoscaled"]]: isAllAxesAutoscaled }) }), small: true, title: "Autoscale", onClick: handleAutoscaleButtonClick }),
        React.createElement(IconButton, { classes: { root: styles.gridIcon }, icon: React.createElement(GridlinesIcon, { className: classNames({
                    [styles["gridIcon-active"]]: isAllGridlinesVisible,
                }) }), small: true, title: "Gridlines", onClick: () => handleGridlinesButtonClick && handleGridlinesButtonClick(!isAllGridlinesVisible) }),
        React.createElement(GraphSourcesLabels, { sources: elementData?.sources }))), [elementData?.sources, someResultBiased, graphResults?.length]);
    const renderConfig = useCallback((axesRagesConfig, handleAutoscaleButtonClick) => !editMode && !elementData.showSettings ? null : (React.createElement(LineGraphElementConfig, { form: form, elementData: elementData, restrictions: restrictions, axesRagesConfig: axesRagesConfig, alternativeFlowsMapping: alternativeFlowsMapping, onLockButtonClick: onLockButtonClick, handleAutoscaleButtonClick: handleAutoscaleButtonClick })), [editMode, elementData, form, onLockButtonClick, restrictions, alternativeFlowsMapping]);
    const onCopyPointData = useCallback((e) => {
        const text = e.points?.reduce((acc, clickedPoint) => {
            const sourceResult = graphResults?.find(({ sourceId }) => sourceId === clickedPoint.customdata);
            const clickedPointX = xAxisIsDiscrete ? Math.floor(Number(clickedPoint.x)) : Number(clickedPoint.x);
            const originalPoint = sourceResult?.points?.find(({ point }) => point?.x === clickedPointX);
            const { point } = { ...originalPoint };
            const { x, median, spread, average, sd } = { ...point };
            const xText = `X - ${parameterName}: ${x}${parameterUnit ? ` ${getTextContentFromHtml(parameterUnit)}` : ""}`;
            const yText = `Y - ${sourceResult?.sourceLineTitle}`;
            const sourceName = getResultElementSourceComputedName(sourceResult?.source?.source ?? null, simulations, optimizations, insights, recipes, persistedInsightsData, processVariables, processData);
            const { simulationId } = { ...sourceResult?.source?.source };
            const recipeId = simulations.find(({ id }) => id === simulationId)?.recipeId;
            const recipe = recipes.find(({ id }) => id === recipeId);
            const customVariablesList = recipe?.state?.customVariables?.filter(({ recordInData }) => Boolean(recordInData)) ?? [];
            const timing = getTimingSummaryText(sourceResult?.source?.source?.timing ?? null, getSimulationTimestampOptions(sourceResult?.source?.source?.simulationId ?? 0, simulations, recipes), processVariables, customVariablesList);
            const unit = sourceResult?.sourceLineUnits
                ? ` ${getTextContentFromHtml(sourceResult?.sourceLineUnits)}`
                : "";
            const mainText = `Median - ${median}${unit}\r\nSpread - ${spread}${unit}\r\nMean - ${average}${unit}\r\nSD - ${sd}${unit}`;
            const pointText = `${xText}\r\n${yText}\r\n${timing}\r\n${sourceName}\r\n${mainText}`;
            return `${acc}\r\n\r\n${pointText}`;
        }, "");
        copyToClipboard(text);
        setShowCopiedToClipboardMessage(true);
    }, [
        graphResults,
        insights,
        optimizations,
        parameterName,
        parameterUnit,
        persistedInsightsData,
        processData,
        processVariables,
        recipes,
        setShowCopiedToClipboardMessage,
        simulations,
        xAxisIsDiscrete,
    ]);
    const createDialogParameterValue = useMemo(() => {
        const name = elementData?.parameter?.parameter?.name ?? "";
        const regularType = elementData?.parameter?.parameter?.type === ResultElementParameterType.RECIPE
            ? GlobalEntityType.Recipe
            : GlobalEntityType.CellModel;
        const regularParameter = {
            name,
            type: regularType,
            value: selectedGraphValue ?? 0,
        };
        const alternativeFlowParameter = {
            name,
            type: ResultElementParameterType.ALTERNATIVE_FLOW,
            flowIndex: selectedGraphValue ?? 0,
        };
        return isAlternativeFlowParameter ? alternativeFlowParameter : regularParameter;
    }, [elementData?.parameter?.parameter, isAlternativeFlowParameter, selectedGraphValue]);
    const renderCreateEntityDialogs = useMemo(() => {
        if (createEntityDialogData.elementId !== elementData.id ||
            createEntityDialogData.sourceId !== createEntityBaseSource?.id) {
            return null;
        }
        return newEntityType === EntitiesCreatableFromResultElement.Variant ? (React.createElement(CreateVariantFromResultElementDialog, { simulationId: (createEntityBaseSource?.source).simulationId ?? 0, adjustmentId: (createEntityBaseSource?.source).adjustmentId ?? 0, parameterValues: [createDialogParameterValue], sourceId: createEntityBaseSource?.id ?? 0, resultElementType: ResultSetElementType.LineGraph, elementId: elementData.id })) : (React.createElement(CreateEntityFromResultElementDialog, { isOpen: true, sourceId: createEntityBaseSource?.id, parameterValues: [createDialogParameterValue], entityType: newEntityType ?? EntitiesCreatableFromResultElement.Recipe, resultElementType: ResultSetElementType.LineGraph, elementId: elementData.id, entityId: (createEntityBaseSource?.source).simulationId ?? 0, onClose: () => dispatch(setCreateEntityFromResultElementDialogData({ elementId: null, sourceId: null })) }));
    }, [
        createEntityDialogData.elementId,
        createEntityDialogData.sourceId,
        elementData.id,
        createEntityBaseSource?.id,
        createEntityBaseSource?.source,
        newEntityType,
        createDialogParameterValue,
        dispatch,
    ]);
    useEffect(() => {
        if (isResultFetching) {
            return;
        }
        if (graphData === null ||
            (prevIsResultFetching && !isResultElementChanging) ||
            (prevIsResultElementChanging && !isResultElementChanging)) {
            setGraphData(prepareLineGraphData(graphResults ?? [], undefined, undefined, alternativeFlowsMapping));
        }
    }, [
        graphData,
        graphResults,
        isResultElementChanging,
        isResultFetching,
        prevIsResultElementChanging,
        prevIsResultFetching,
        alternativeFlowsMapping,
    ]);
    useEffect(() => {
        return () => {
            if (updatedRangeConfigsRef.current) {
                dispatch(setPlotRangeConfigs({
                    resultSetId,
                    elementId: elementData.id,
                    ranges: updatedRangeConfigsRef.current,
                }));
            }
        };
    }, [resultSetId, elementData.id, dispatch]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.root },
            React.createElement(ResultInfoMessage, { type: ResultInfoMessageType.COPIED_TO_CLIPBOARD, visible: showCopiedToClipboardMessage, onClose: () => setShowCopiedToClipboardMessage(false), className: styles["copied-to-clipboard-message"] }),
            !editMode && (React.createElement(GraphElementGraph, { plotLayout: plotLayout, graphData: graphData ?? [], onPlotHover: onPlotHover, onRelayout: handleRelayout, renderHeader: renderHeader, renderConfig: renderConfig, getActionMenuItems: pageInEditMode ? getActionMenuItems : undefined, onCopy: onCopyPointData })),
            editMode && renderConfig()),
        renderCreateEntityDialogs));
};
export default LineGraphElement;
